import * as React from 'react'
import { Mutation, MutationFunction, Query } from 'react-apollo'

import { ApolloError } from 'apollo-client'
import { PageContext } from 'main/components/common/Page'
import { User, USER_QUERY } from 'main/gql/user'
import { Flags, useFeatureFlag } from 'main/hooks/useFeatureFlag'
import { errorAlert, IPageAlert } from 'main/util/page'
import segment from 'util/analytics/segment'

import {
  CREATE_SAVED_SEARCH_MUTATION,
  CreateSavedSearch,
  CreateSavedSearchVariables,
  SearchPage_result_location,
} from '../../gql'
import ConfrimationForm from './ConfirmationForm'
import LoggedInForm from './LoggedInForm'
import LoggedOutForm from './LoggedOutForm'
import LoggedOutFormSSO from './LoggedOutFormSSO'
import { SaveSearchFormContainer } from './SaveSearchForm.styles'
import ValueProp from './ValueProp'

interface IProps {
  location: SearchPage_result_location | null
  loggedOutTitle: string
}

export default function SaveSearchForm(props: IProps) {
  const { location, loggedOutTitle } = props

  const [displayForm, setDisplayForm] = React.useState<boolean>(true)
  const [searchSaved, setSearchSaved] = React.useState<boolean>(false)

  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  if (!displayForm) return null
  else if (searchSaved) {
    return (
      <SaveSearchFormContainer>
        <ConfrimationForm location={location} setDisplayForm={setDisplayForm} />
      </SaveSearchFormContainer>
    )
  } else {
    return (
      <PageContext.Consumer>
        {({ setAlert }) => (
          <Query<User> query={USER_QUERY}>
            {({ data }) => {
              if (!data) return null

              const { user } = data

              return (
                <>
                  <ValueProp />
                  <Mutation<CreateSavedSearch, CreateSavedSearchVariables>
                    mutation={CREATE_SAVED_SEARCH_MUTATION}
                    onCompleted={() => setSearchSaved(true)}
                    onError={e => handleError(e, setAlert, setSearchSaved)}
                  >
                    {(mutate, { loading }) => (
                      <SaveSearchFormContainer>
                        {user ? (
                          <LoggedInForm
                            loading={loading}
                            handleSubmit={() => handleSubmit(mutate, location, setAlert)}
                          />
                        ) : isSSO ? (
                          <LoggedOutFormSSO loggedOutTitle={loggedOutTitle} />
                        ) : (
                          <LoggedOutForm
                            loggedOutTitle={loggedOutTitle}
                            onComplete={() => handleSubmit(mutate, location, setAlert)}
                            setAlert={setAlert}
                          />
                        )}
                      </SaveSearchFormContainer>
                    )}
                  </Mutation>
                </>
              )
            }}
          </Query>
        )}
      </PageContext.Consumer>
    )
  }
}

function handleError(
  error: ApolloError,
  setAlert: (alert: IPageAlert | null) => void,
  setSearchSaved: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const messages = error.graphQLErrors.map(e => e.message)

  if (messages.includes('user_id_state_city has already been taken')) {
    setSearchSaved(true)
  } else setAlert(errorAlert)
}

function handleSubmit(
  mutate: MutationFunction<CreateSavedSearch, CreateSavedSearchVariables>,
  location: SearchPage_result_location | null,
  setAlert: (alert: IPageAlert | null) => void,
) {
  if (!location || !location.city || !location.state) {
    setAlert({ body: 'You have not searched for anything yet.', kind: 'danger' })
    return
  }
  const { city, state, latitude, longitude } = location

  trackSaveSearchEvent(location)

  mutate({
    variables: {
      city,
      latitude,
      longitude,
      state,
    },
  })
}

function trackSaveSearchEvent(location: SearchPage_result_location | null) {
  segment.track('Saved Search', {
    city: location?.city,
    latitude: location?.latitude,
    longitude: location?.longitude,
    state: location?.state,
  })
}
